import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAutoRegistration } from "@/hooks/useAutoRegistration";
import { supabase } from "@/integrations/supabase/client";
import { trackGA4Event } from "@/lib/ga4";
import { 
  Home, 
  TrendingUp, 
  Target, 
  Users, 
  FileText, 
  CheckCircle,
  DollarSign,
  Clock,
  ChevronRight,
  Zap,
  Shield,
  Banknote,
} from "lucide-react";
import { SEOHead } from "@/components/seo";
import { generateServiceSchema, generateLocalBusinessSchema } from "@/components/seo/SchemaMarkup";
import { trackPixelLead } from "@/lib/retargeting";
import { useAgentBranding } from "@/hooks/useAgentBranding";
import AgentBrandingCard from "@/components/agent/AgentBrandingCard";

const Sell = () => {
  useEffect(() => {
    document.title = "Sell Your Home in South Jersey | Prime Realty Partners";
  }, []);

  const { brandedAgent, isBranded, agentSlug } = useAgentBranding();
  const { toast } = useToast();
  const { autoRegisterLead } = useAutoRegistration();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    bedrooms: "",
    bathrooms: "",
    sqft: "",
    tcpa_consent: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitting(true);
    try {
      const { error: dbError } = await supabase.from("seller_leads").insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "",
        address: formData.address,
        city: formData.city || null,
        state: formData.state || null,
        zip_code: formData.zip_code || null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        sqft: formData.sqft ? parseInt(formData.sqft) : null,
        tcpa_consent: formData.tcpa_consent,
        consent_timestamp: formData.tcpa_consent ? new Date().toISOString() : null,
      });

      if (dbError) throw dbError;

      // Sync to FUB
      try {
        await supabase.functions.invoke("submit-lead", {
          body: {
            event_type: "seller_lead",
            lead_data: {
              name: formData.name,
              email: formData.email,
              phone: formData.phone || "",
              tcpa_consent: formData.tcpa_consent,
              consent_timestamp: formData.tcpa_consent ? new Date().toISOString() : null,
              source_page: window.location.href,
            },
            listing_data: {
              address: formData.address,
              city: formData.city,
              state: formData.state,
              zip_code: formData.zip_code,
            },
            metadata: {
              ...(agentSlug && { assign_to_agent: true, agent_slug: agentSlug }),
            },
          },
        });
      } catch (fubError) {
        console.log("FUB sync skipped or failed:", fubError);
      }

      toast({
        title: "Request Submitted!",
        description: "We'll contact you shortly with your home valuation.",
      });

      // Track Lead conversion for retargeting pixels
      trackPixelLead({
        formType: "seller_valuation",
        contentName: "Sell Page - Home Valuation Request",
      });

      trackGA4Event('form_submit', { event_category: 'lead_action', form_name: 'seller_valuation', form_page: window.location.pathname });

      // Auto-register the lead
      await autoRegisterLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        bedrooms: "",
        bathrooms: "",
        sqft: "",
        tcpa_consent: false,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: "Expert Market Analysis",
      description: "We provide comprehensive market analysis to price your home competitively.",
    },
    {
      icon: Users,
      title: "Maximum Exposure",
      description: "Access to our network of qualified buyers and partner agents.",
    },
    {
      icon: Target,
      title: "Expert Marketing Tactics",
      description: "Strategic marketing campaigns to attract qualified buyers and maximize visibility.",
    },
    {
      icon: FileText,
      title: "Full Service Support",
      description: "From listing to closing, we handle every detail for you.",
    },
  ];

  const process = [
    { step: 1, title: "Free Consultation", description: "We'll evaluate your home and discuss your goals." },
    { step: 2, title: "Strategic Pricing", description: "Data-driven pricing to attract the right buyers." },
    { step: 3, title: "Marketing Launch", description: "Professional photos, listings, and targeted marketing." },
    { step: 4, title: "Consistent Communication", description: "Regular updates and feedback to keep you informed every step of the way." },
    { step: 5, title: "Showings & Offers", description: "We manage showings and negotiate the best offers." },
    { step: 6, title: "Close the Deal", description: "We guide you through closing for a smooth transaction." },
  ];

  const sellFaqs = [
    {
      question: "How fast can I sell my house in South Jersey?",
      answer: "With a cash offer, you can close in as little as 7-14 days. A traditional listing typically takes 30-60 days from listing to close, depending on market conditions in your area.",
    },
    {
      question: "Can I sell my house as-is in NJ?",
      answer: "Yes. You can sell your home in any condition in New Jersey. Many buyers, especially investors, purchase homes as-is. You are still required to disclose known material defects per NJ seller disclosure laws.",
    },
    {
      question: "Do I have to pay capital gains tax when I sell my house?",
      answer: "If you've lived in the home as your primary residence for at least 2 of the last 5 years, you can exclude up to $250,000 in gains (single) or $500,000 (married filing jointly) from capital gains tax. Consult a tax professional for your specific situation. NJ also has an exit tax (estimated tax payment) of 2% of the sale price for sellers leaving the state.",
    },
    {
      question: "How much are seller closing costs in NJ?",
      answer: "Sellers in NJ typically pay 6-8% of the sale price, including agent commissions, attorney fees, and the NJ Realty Transfer Fee. See our complete Closing Costs Guide for details.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: sellFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const serviceSchema = generateServiceSchema();
  const localBusinessSchema = generateLocalBusinessSchema();

  return (
    <Layout>
      <SEOHead
        title="Sell Your Home in South Jersey | Prime Realty Partners"
        description="Sell your South Jersey home with confidence. Free home valuation and expert guidance from Prime Realty Partners. Cherry Hill, Moorestown & more."
        canonicalUrl="https://www.prpsj.com/sell"
        keywords="sell my house fast south jersey, sell my house fast nj, sell house as is nj, home valuation south jersey, what is my home worth, sell home New Jersey, home valuation NJ, list my house NJ, real estate agent to sell home, Prime Realty Partners"
        schema={[serviceSchema, localBusinessSchema, faqSchema]}
        noindex={isBranded}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Sell", url: "https://www.prpsj.com/sell" }]}
      />

      {/* Agent Branding Card (when branded) */}
      {isBranded && brandedAgent && (
        <section className="py-8 bg-muted/30">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <AgentBrandingCard agent={brandedAgent} />
            </div>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/10 to-primary/5 py-20">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                Sell Your Home With Confidence
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                Get the best price for your home with Prime Realty Partners. 
                Our experienced agents and proven marketing strategies ensure 
                a smooth sale.
              </p>
              <div className="flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <span>Top Dollar Pricing</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <span>Fast Results</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  <span>Full Service</span>
                </div>
              </div>
            </div>

            {/* Valuation Form */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5 text-primary" />
                  Get Your Free Home Valuation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">Property Address *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Main Street"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP</Label>
                      <Input
                        id="zip"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Bedrooms</Label>
                      <Select 
                        value={formData.bedrooms} 
                        onValueChange={(v) => setFormData({ ...formData, bedrooms: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6].map((n) => (
                            <SelectItem key={n} value={n.toString()}>{n}+</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Bathrooms</Label>
                      <Select 
                        value={formData.bathrooms} 
                        onValueChange={(v) => setFormData({ ...formData, bathrooms: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 1.5, 2, 2.5, 3, 3.5, 4].map((n) => (
                            <SelectItem key={n} value={n.toString()}>{n}+</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sqft">Sq Ft</Label>
                      <Input
                        id="sqft"
                        type="number"
                        value={formData.sqft}
                        onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                        placeholder="2000"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent"
                      checked={formData.tcpa_consent}
                      onCheckedChange={(checked) => 
                        setFormData({ ...formData, tcpa_consent: checked as boolean })
                      }
                    />
                    <Label htmlFor="consent" className="text-xs text-muted-foreground leading-tight">
                      By submitting this form, you agree to be contacted by MarChris Ventures LLC dba Prime Realty Partners via call, email, and text regarding real estate and mortgage services, including property updates, appointment reminders, and transactional communications. Message frequency may vary. Message and data rates may apply. You may opt out at any time by replying STOP, or reply HELP for assistance.
                      <br />
                      Consent is not a condition of purchase.
                      <br />
                      <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>
                    </Label>
                  </div>

                  <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                    {submitting ? "Submitting..." : "Get My Free Valuation"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <h2 className="font-heading text-3xl font-bold text-center mb-10">
            Why Sell With Prime Realty Partners?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-muted/50">
        <div className="container">
          <h2 className="font-heading text-3xl font-bold text-center mb-10">
            Our Selling Process
          </h2>
          <div className="max-w-4xl mx-auto">
            {process.map((step, index) => (
              <div key={index} className="flex items-start gap-4 mb-8 last:mb-0">
                <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">
                  {step.step}
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold mb-1">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sell Fast Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-6">
              Sell My House Fast in South Jersey
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Whether you need to sell quickly due to relocation, divorce, inheritance, or financial reasons, we can help you explore all your options. In South Jersey, you have several paths to selling your home — from a traditional listing that maximizes your sale price to a fast cash offer that closes in as little as 2 weeks. Our agents help you evaluate which option makes the most sense for your situation.
            </p>
          </div>
        </div>
      </section>

      {/* Selling Options */}
      <section className="py-16 bg-muted/50">
        <div className="container">
          <h2 className="font-heading text-3xl font-bold text-center mb-10">
            Your Selling Options
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">Traditional Listing</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  List your home on the MLS with a PRP agent. Professional photos, marketing, and expert negotiation to get top dollar.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Typical timeline: 30-60 days to close</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span>Best for: maximum sale price with time</span>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">Sell As-Is</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  List your home in its current condition — no repairs, no renovations, no staging. We market to investors and cash buyers who specialize in as-is purchases.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Typical timeline: 21-45 days</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span>Best for: no repairs needed</span>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">Cash Offer</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get a competitive cash offer on your home with no contingencies, no appraisals, and a closing date you choose.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>Typical timeline: 7-21 days</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle className="h-4 w-4 text-primary" />
                  <span>Best for: speed and certainty</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-center mb-10">
              Frequently Asked Questions About Selling in NJ
            </h2>
            <div className="space-y-4">
              {sellFaqs.map((faq, index) => (
                <details
                  key={index}
                  className="group border border-border rounded-lg"
                >
                  <summary className="flex items-center justify-between cursor-pointer p-5 font-heading font-semibold text-foreground hover:text-primary transition-colors">
                    {faq.question}
                    <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-5 text-muted-foreground leading-relaxed">
                    {faq.answer}
                    {faq.question.includes("closing costs") && (
                      <span>
                        {" "}
                        <Link to="/closing-costs-nj" className="text-primary underline hover:text-primary/80">
                          View our complete NJ Closing Costs Guide
                        </Link>
                      </span>
                    )}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Sell;
